import React, { useMemo, useState } from "react";
import { Check, School } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SavedGroups } from "./SavedGroups";

const Quick = ({ label, onClick, testid, active }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${active ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{label}</button>
);

const SchoolSetter = ({ s, onSet }) => {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState("");
  const save = async () => {
    if (!v.trim()) return;
    try { await api.post("/teacher/diary/school", { student_id: s.id, school: v }); onSet(s.id, v.trim()); setOpen(false); }
    catch { toast.error("Could not save the school"); }
  };
  if (!open) return <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="text-[10px] text-blue-700 underline" data-testid={`set-school-${s.id}`}>+ school</button>;
  return (
    <span className="flex gap-1" onClick={(e) => e.stopPropagation()}>
      <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="School name"
        className="h-6 w-28 rounded border border-slate-300 px-1 text-[11px]" data-testid={`school-input-${s.id}`} />
      <button type="button" onClick={save} className="text-[11px] font-bold text-emerald-700" data-testid={`school-save-${s.id}`}>Save</button>
    </span>
  );
};

export const StudentPicker = ({ students, value, onChange, onSchoolSet, taken }) => {
  const sel = new Set(value);
  const busy = taken || new Set();
  const free = students.filter((s) => !busy.has(s.id));
  const stds = useMemo(() => [...new Set(students.map((s) => s.student_class).filter(Boolean))], [students]);
  const schools = useMemo(() => [...new Set(students.map((s) => s.school).filter(Boolean))], [students]);
  const pick = (fn) => onChange(free.filter(fn).map((s) => s.id));
  const add = (fn) => onChange([...new Set([...value, ...free.filter(fn).map((s) => s.id)])]);
  const toggle = (id) => { if (!busy.has(id)) onChange(sel.has(id) ? value.filter((x) => x !== id) : [...value, id]); };
  return (
    <div className="space-y-2" data-testid="student-picker">
      <div className="flex flex-wrap gap-1.5">
        {taken ? <Quick label={`Rest of the class (${free.length})`} onClick={() => pick(() => true)} testid="pick-rest" active={value.length === free.length && free.length > 0} />
          : <Quick label={`All (${students.length})`} onClick={() => pick(() => true)} testid="pick-all" active={value.length === students.length && students.length > 0} />}
        {stds.length > 1 && stds.map((c) => <Quick key={c} label={c} onClick={() => add((s) => s.student_class === c)} testid={`pick-std-${c}`} />)}
        {schools.map((sc) => <Quick key={sc} label={sc} onClick={() => add((s) => s.school === sc)} testid={`pick-school-${sc}`} />)}
        <Quick label="Clear" onClick={() => onChange([])} testid="pick-clear" />
      </div>
      <SavedGroups value={value} free={new Set(free.map((s) => s.id))} onPick={(ids) => onChange([...new Set([...value, ...ids])])} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto">
        {students.map((s) => (
          <div key={s.id} role="button" tabIndex={0} onClick={() => toggle(s.id)} data-testid={`pick-student-${s.id}`}
            className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-left text-sm transition-colors ${busy.has(s.id) ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-50" : sel.has(s.id) ? "cursor-pointer border-emerald-500 bg-emerald-50" : "cursor-pointer border-slate-200 bg-white hover:bg-slate-50"}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${sel.has(s.id) ? "bg-emerald-600 text-white" : "border border-slate-300"}`}>{sel.has(s.id) && <Check className="h-3.5 w-3.5" />}</span>
            <span className="min-w-0">
              <span className="block truncate font-medium text-slate-800">{s.name}{busy.has(s.id) && <span className="ml-1 text-[10px] font-bold text-emerald-700">planned</span>}</span>
              <span className="flex items-center gap-1 text-[11px] text-slate-500">{s.student_class}{s.school ? <><School className="h-3 w-3" />{s.school}</> : <SchoolSetter s={s} onSet={onSchoolSet} />}</span>
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500" data-testid="picked-count">{value.length} selected</p>
    </div>
  );
};
